import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Modal,
  Form,
  Table,
  Spinner,
  Alert,
  Badge,
  ListGroup,
  Card,
  InputGroup,
  Tab,
  Tabs,
  Row,
  Col,
} from "react-bootstrap";
import html2pdf from "html2pdf.js";
import {
  get_affiliates_action,
  update_affiliate_status_action,
  record_payout_action,
  update_campaign_settings_action,
  create_affiliate_action,
  searchUsersAPI,
} from "../reduxData/user/userAction";
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";

const UserSearchInput = ({ onSelect }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.length > 2 && !selectedUser) {
        setLoading(true);
        const users = await searchUsersAPI(query);
        setResults(users);
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [query, selectedUser]);

  const handleSelect = (user) => {
    setSelectedUser(user);
    setResults([]);
    setQuery(user.email);
    onSelect(user);
  };

  const clearSelection = () => {
    setSelectedUser(null);
    setQuery("");
    onSelect(null);
  };

  return (
    <div className="position-relative">
      <InputGroup>
        <Form.Control
          placeholder="Search by name or email..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedUser(null);
          }}
          disabled={!!selectedUser}
        />
        {selectedUser && (
          <Button variant="outline-danger" onClick={clearSelection}>
            X
          </Button>
        )}
      </InputGroup>

      {results.length > 0 && !selectedUser && (
        <ListGroup
          className="position-absolute w-100 shadow-sm"
          style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}
        >
          {results.map((user) => (
            <ListGroup.Item
              key={user._id}
              action
              onClick={() => handleSelect(user)}
            >
              <strong>{user.user_name}</strong>{" "}
              <small className="text-muted">({user.email})</small>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {loading && <small className="text-muted">Searching...</small>}
    </div>
  );
};

const CreateAffiliateModal = ({ show, onHide, onCreate }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [code, setCode] = useState("");

  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const [legalName, setLegalName] = useState("");
  const [legalAddress, setLegalAddress] = useState("");
  const [entityType, setEntityType] = useState("Individual");

  const [loading, setLoading] = useState(false);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    if (user) {
      setContactEmail(user.email || "");
      setContactPhone(user.phoneNumber || "");
      setLegalName(user.name || user.user_name || "");
    } else {
      setContactEmail("");
      setContactPhone("");
      setLegalName("");
    }
  };

  const handleSubmit = async () => {
    if (
      !selectedUser ||
      !code ||
      !contactEmail ||
      !contactPhone ||
      !legalName ||
      !legalAddress
    )
      return alert("Please fill in all required fields.");

    setLoading(true);

    const payload = {
      email: selectedUser.email,
      referralCode: code,
      contactInfo: {
        email: contactEmail,
        phoneNumber: contactPhone,
      },
      legalInfo: {
        legalName: legalName,
        address: legalAddress,
        entityType: entityType,
      },
    };

    await onCreate(payload);

    setLoading(false);
    setCode("");
    setContactEmail("");
    setContactPhone("");
    setLegalName("");
    setLegalAddress("");
    setEntityType("Individual");
    setSelectedUser(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Manually Create Affiliate</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="small mb-3">
          Search for a user to auto-fill details. Verify all Legal and Contact
          info before creating.
        </Alert>

        <div className="row">
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">Search User</Form.Label>
              <UserSearchInput onSelect={handleUserSelect} />
            </Form.Group>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">Referral Code *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. SUMMER2025"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Form.Group>

            <div className="p-3 bg-light rounded border mb-3 mb-md-0">
              <h6 className="small text-muted fw-bold border-bottom pb-2 mb-3">
                Contact Details (Required)
              </h6>
              <Form.Group className="mb-2">
                <Form.Label className="small">Contact Email</Form.Label>
                <Form.Control
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small">Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="+1..."
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>

          <div className="col-md-6">
            <div className="p-3 bg-light rounded border h-100">
              <h6 className="small text-muted fw-bold border-bottom pb-2 mb-3">
                Legal Information (Required)
              </h6>

              <Form.Group className="mb-2">
                <Form.Label className="small">Legal Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Individual or Company Name"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small">Entity Type</Form.Label>
                <Form.Select
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                  className="form-control"
                >
                  <option value="Individual">Individual</option>
                  <option value="LLC">LLC</option>
                  <option value="Corporation">Corporation</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small">Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Full legal address..."
                  value={legalAddress}
                  onChange={(e) => setLegalAddress(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            loading ||
            !selectedUser ||
            !code ||
            !legalName ||
            !legalAddress ||
            !contactEmail
          }
        >
          {loading ? <Spinner size="sm" /> : "Create Affiliate"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ReviewApplicationModal = ({ show, onHide, application, onReview }) => {
  const [decision, setDecision] = useState("approved");
  const [reason, setReason] = useState("");
  const [grantBadge, setGrantBadge] = useState(false);
  const [programType, setProgramType] = useState("partner");
  const [loading, setLoading] = useState(false);

  const [campaignStart, setCampaignStart] = useState("");
  const [campaignEnd, setCampaignEnd] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [rules, setRules] = useState({
    signupReward: { enabled: false, amount: 0 },
    buyerPremiumReward: { enabled: false, percentage: 0 },
    affiliateListingReward: { enabled: false, type: "fixed", value: 0 },
    referralListingReward: { enabled: false, type: "fixed", value: 0 },
  });

  useEffect(() => {
    if (show && application) {
      setDecision("approved");
      setReason("");
      setGrantBadge(false);
      setCampaignStart(new Date().toISOString().split("T")[0]);
      setCampaignEnd("");
      setAdminNotes("");
      setRules({
        signupReward: { enabled: false, amount: 0 },
        buyerPremiumReward: { enabled: false, percentage: 0 },
        affiliateListingReward: { enabled: false, type: "fixed", value: 0 },
        referralListingReward: { enabled: false, type: "fixed", value: 0 },
      });

      const isStore =
        application.applicationDetails?.promotionMethod?.startsWith("[STORES]");
      setProgramType(isStore ? "storefront" : "partner");
    }
  }, [show, application]);

  const updateRule = (category, field, value) => {
    setRules((prev) => ({
      ...prev,
      [category]: { ...prev[category], [field]: value },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    const reviewData = {
      decision,
      reason: decision === "rejected" ? reason : null,
      grantBadge,
      programType,
      campaignStartDate: campaignStart,
      campaignEndDate: campaignEnd,
      adminNotes: adminNotes,
      commissionRules: rules,
    };
    await onReview(application._id, reviewData);
    setLoading(false);
    onHide();
  };

  if (!application) return null;

  const {
    userId,
    contactInfo,
    legalInfo,
    companyInfo,
    applicationDetails,
    referralCode,
    createdAt,
  } = application;

  const inferredLabel = applicationDetails?.promotionMethod?.startsWith(
    "[STORES]",
  )
    ? "Tag Team (Store)"
    : "Partner Program (Creator)";

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title>Review Affiliate Application</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {/* --- Header Summary --- */}
        <div className="d-flex align-items-center justify-content-between mb-4 p-3 bg-light rounded">
          <div className="d-flex align-items-center gap-3">
            <img
              src={userId?.profile_pic || "https://via.placeholder.com/50"}
              alt="profile"
              className="rounded-circle"
              style={{ width: "60px", height: "60px", objectFit: "cover" }}
            />
            <div>
              <h5 className="mb-0">{userId?.user_name}</h5>
              <small className="text-muted">Registered User</small>
            </div>
          </div>
          <div className="text-end">
            <h6 className="mb-0 text-muted">Requested Code:</h6>
            <h3 className="text-primary fw-bold mb-0">{referralCode}</h3>
            <div className="mt-1">
              <Badge
                bg={inferredLabel.includes("Store") ? "info" : "warning"}
                text="dark"
              >
                {inferredLabel}
              </Badge>
            </div>
            <small className="text-muted d-block mt-1">
              Applied: {new Date(createdAt).toLocaleDateString()}
            </small>
          </div>
        </div>

        <Row className="g-4">
          <Col md={5}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <h6 className="fw-bold border-bottom pb-2 mb-3">
                  Contact Information
                </h6>
                <div className="mb-3">
                  <label className="small text-muted fw-bold">
                    CONTACT EMAIL
                  </label>
                  <div>{contactInfo?.email}</div>
                </div>
                <div className="mb-3">
                  <label className="small text-muted fw-bold">
                    CONTACT PHONE
                  </label>
                  <div>{contactInfo?.phoneNumber}</div>
                </div>

                <h6 className="fw-bold border-bottom pb-2 mb-3 mt-4">
                  Legal Information
                </h6>
                <div className="mb-3">
                  <label className="small text-muted fw-bold">LEGAL NAME</label>
                  <div>{legalInfo?.legalName}</div>
                </div>
                <div className="mb-3">
                  <label className="small text-muted fw-bold">
                    ENTITY TYPE
                  </label>
                  <div>{legalInfo?.entityType}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={7}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <h6 className="fw-bold border-bottom pb-2 mb-3">
                  Promotional Strategy
                </h6>
                <div className="mb-4">
                  <label className="small text-muted fw-bold">
                    TARGET AUDIENCE
                  </label>
                  <p className="mb-0">{applicationDetails?.targetAudience}</p>
                </div>
                <div className="mb-4">
                  <label className="small text-muted fw-bold">
                    PROMOTION METHOD
                  </label>
                  <p className="mb-0 p-2 bg-light rounded">
                    {applicationDetails?.promotionMethod}
                  </p>
                </div>
                {applicationDetails?.websiteUrl && (
                  <div className="mb-4">
                    <label className="small text-muted fw-bold">WEBSITE</label>
                    <div>
                      <a
                        href={applicationDetails.websiteUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {applicationDetails.websiteUrl}
                      </a>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="mt-4 p-4 border-top">
          <h5 className="mb-3">Action & Configuration</h5>
          <Form>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold small text-muted">
                    DECISION
                  </Form.Label>
                  <Form.Select
                    value={decision}
                    onChange={(e) => setDecision(e.target.value)}
                    size="lg"
                  >
                    <option value="approved">Approve</option>
                    <option value="rejected">Reject</option>
                  </Form.Select>
                </Form.Group>

                {decision === "approved" && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-muted">
                      ASSIGN PROGRAM TYPE
                    </Form.Label>
                    <Form.Select
                      value={programType}
                      onChange={(e) => setProgramType(e.target.value)}
                    >
                      <option value="partner">Partner (Revenue Share)</option>
                      <option value="storefront">Tag-Team Storefront</option>
                      <option value="affiliate">
                        Standard Affiliate (Private Only)
                      </option>
                    </Form.Select>
                  </Form.Group>
                )}
              </Col>

              <Col md={8}>
                {decision === "rejected" && (
                  <Form.Group className="mb-3">
                    <Form.Label>Rejection Reason</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Explain why..."
                    />
                  </Form.Group>
                )}

                {decision === "approved" && (
                  <>
                    <Form.Group className="mb-3 p-3 border rounded bg-white">
                      <Form.Check
                        type="checkbox"
                        id="grantBadge"
                        label={
                          <span>
                            Grant <strong>Tag Team Partner Badge</strong>? 🏅
                          </span>
                        }
                        checked={grantBadge}
                        onChange={(e) => setGrantBadge(e.target.checked)}
                      />
                    </Form.Group>

                    <div className="border rounded p-3 bg-light">
                      <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary">
                        Terms & Configuration
                      </h6>

                      <Row className="g-2 mb-3">
                        <Col md={6}>
                          <Form.Label className="small fw-bold">
                            Start Date
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={campaignStart}
                            onChange={(e) => setCampaignStart(e.target.value)}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Label className="small fw-bold">
                            End Date (Optional)
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={campaignEnd}
                            onChange={(e) => setCampaignEnd(e.target.value)}
                          />
                        </Col>
                      </Row>

                      <div className="bg-white p-3 rounded border">
                        <label className="small text-muted fw-bold d-block mb-2">
                          COMMISSION RULES
                        </label>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Check
                            type="switch"
                            label="Signup Reward ($)"
                            checked={rules.signupReward.enabled}
                            onChange={(e) =>
                              updateRule(
                                "signupReward",
                                "enabled",
                                e.target.checked,
                              )
                            }
                          />
                          {rules.signupReward.enabled && (
                            <InputGroup size="sm" style={{ width: "120px" }}>
                              <InputGroup.Text>$</InputGroup.Text>
                              <Form.Control
                                type="number"
                                placeholder="0"
                                value={
                                  rules.signupReward.amount === 0
                                    ? ""
                                    : rules.signupReward.amount
                                }
                                onChange={(e) =>
                                  updateRule(
                                    "signupReward",
                                    "amount",
                                    e.target.value === ""
                                      ? 0
                                      : Number(e.target.value),
                                  )
                                }
                              />
                            </InputGroup>
                          )}
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Check
                            type="switch"
                            label="Buyer Premium Share (%)"
                            checked={rules.buyerPremiumReward.enabled}
                            onChange={(e) =>
                              updateRule(
                                "buyerPremiumReward",
                                "enabled",
                                e.target.checked,
                              )
                            }
                          />
                          {rules.buyerPremiumReward.enabled && (
                            <InputGroup size="sm" style={{ width: "120px" }}>
                              <Form.Control
                                type="number"
                                placeholder="0"
                                value={
                                  rules.buyerPremiumReward.percentage === 0
                                    ? ""
                                    : rules.buyerPremiumReward.percentage
                                }
                                onChange={(e) =>
                                  updateRule(
                                    "buyerPremiumReward",
                                    "percentage",
                                    e.target.value === ""
                                      ? 0
                                      : Number(e.target.value),
                                  )
                                }
                              />
                              <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup>
                          )}
                        </div>

                        <div className="d-flex flex-column mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <Form.Check
                              type="switch"
                              label="Affiliate Listing Reward"
                              checked={rules.affiliateListingReward.enabled}
                              onChange={(e) =>
                                updateRule(
                                  "affiliateListingReward",
                                  "enabled",
                                  e.target.checked,
                                )
                              }
                            />
                          </div>
                          {rules.affiliateListingReward.enabled && (
                            <div className="d-flex gap-2 mt-2 ps-4">
                              <Form.Select
                                size="sm"
                                value={rules.affiliateListingReward.type}
                                onChange={(e) =>
                                  updateRule(
                                    "affiliateListingReward",
                                    "type",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="fixed">Fixed ($)</option>
                                <option value="percentage">
                                  % of Buyer's Premium
                                </option>
                              </Form.Select>
                              <Form.Control
                                size="sm"
                                type="number"
                                placeholder="0"
                                value={
                                  rules.affiliateListingReward.value === 0
                                    ? ""
                                    : rules.affiliateListingReward.value
                                }
                                onChange={(e) =>
                                  updateRule(
                                    "affiliateListingReward",
                                    "value",
                                    e.target.value === ""
                                      ? 0
                                      : Number(e.target.value),
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <Form.Group className="mt-3">
                        <Form.Label className="small fw-bold">
                          Internal Admin Notes
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </>
                )}
              </Col>
            </Row>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0 pb-4 pe-4">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant={decision === "approved" ? "success" : "danger"}
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 fw-bold"
        >
          {loading
            ? "Processing..."
            : `Confirm ${decision === "approved" ? "Approval" : "Rejection"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ManageAffiliateModal = ({
  show,
  onHide,
  affiliate,
  onUpdateSettings,
  onRecordPayout,
  onStatusChange,
  refreshParent,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedOverviewTab, setSelectedOverviewTab] = useState("signups");

  const [stats, setStats] = useState(null);
  const [details, setDetails] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [voidingLogId, setVoidingLogId] = useState(null);

  const [payoutAmount, setPayoutAmount] = useState(0);
  const [payoutMethod, setPayoutMethod] = useState("paypal");
  const [payoutRef, setPayoutRef] = useState("");
  const [isPaying, setIsPaying] = useState(false);

  const [campaignStart, setCampaignStart] = useState("");
  const [campaignEnd, setCampaignEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [rules, setRules] = useState({
    signupReward: { enabled: false, amount: 0 },
    buyerPremiumReward: { enabled: false, percentage: 0 },
    affiliateListingReward: { enabled: false, type: "fixed", value: 0 },
    referralListingReward: { enabled: false, type: "fixed", value: 0 },
  });

  const fetchAnalytics = async () => {
    if (!affiliate) return;
    setLoadingStats(true);
    try {
      const res = await apiClient.get(
        `admin/affiliate/${affiliate._id}/analytics`,
      );
      if (res.data.status) {
        setStats(res.data.stats);
        setDetails(res.data.details);
        setPayoutAmount(res.data.stats.availableBalance || 0);
      }
    } catch (error) {
      console.error("Failed to fetch stats", error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (show && affiliate) {
      setActiveTab("overview");
      setSelectedOverviewTab("signups");
      fetchAnalytics();

      setCampaignStart(
        affiliate.campaignStartDate
          ? new Date(affiliate.campaignStartDate).toISOString().split("T")[0]
          : "",
      );
      setCampaignEnd(
        affiliate.campaignEndDate
          ? new Date(affiliate.campaignEndDate).toISOString().split("T")[0]
          : "",
      );
      setNotes(affiliate.adminNotes || "");
      if (affiliate.commissionRules) {
        setRules((prev) => ({ ...prev, ...affiliate.commissionRules }));
      }
    }
  }, [show, affiliate]);

  const handleVoidCommission = async (logId) => {
    if (
      !window.confirm(
        "Are you sure you want to VOID this commission? This will permanently remove the funds from the affiliate's balance.",
      )
    ) {
      return;
    }

    setVoidingLogId(logId);
    try {
      const res = await apiClient.put(
        `admin/affiliate/commission/${logId}/void`,
      );
      if (res.data.status) {
        toast.success("Commission successfully voided.");
        await fetchAnalytics();
        if (refreshParent) refreshParent();
      }
    } catch (error) {
      console.error("Void error:", error);
      toast.error(error.response?.data?.message || "Failed to void commission");
    } finally {
      setVoidingLogId(null);
    }
  };

  const downloadContractPDF = () => {
    if (!affiliate) return;

    const legalName = affiliate.legalInfo?.legalName || "Affiliate";
    const entityType = affiliate.legalInfo?.entityType || "Individual";
    const address = affiliate.legalInfo?.address || "Address on File";
    const programType = affiliate.programType || "affiliate";

    const signedAt = affiliate.contract?.signedAt
      ? new Date(affiliate.contract.signedAt).toLocaleDateString()
      : new Date().toLocaleDateString();

    const signedName = affiliate.contract?.signedName || legalName;
    const ipAddress = affiliate.contract?.ipAddress || "Recorded";

    const check = (condition) => (condition ? "☑" : "☐");

    const element = document.createElement("div");
    element.innerHTML = `
      <div style="font-family: 'Times New Roman', Times, serif; font-size: 14px; line-height: 1.6; color: #000; padding: 40px; background-color: #fff;">
        <div style="text-align: center; margin-bottom: 24px;">
          <h4 style="font-weight: bold; margin-bottom: 4px; font-size: 18px;">INSERTBID, LLC</h4>
          <h5 style="font-weight: bold; text-decoration: underline; font-size: 16px;">AFFILIATE & PARTNER PROGRAM AGREEMENT</h5>
        </div>
        <p>
          This Affiliate & Partner Program Agreement (“Agreement”) is entered into as of
          <span style="font-weight: bold; margin: 0 4px;">${signedAt}</span>
          [Effective Date], by and between InsertBid, LLC, a Texas limited liability company, with its principal place of business at
          <span style="font-weight: bold; margin: 0 4px;">Houston, TX</span> (“InsertBid” or the “Company”), and:
        </p>
        <div style="margin: 16px 0; padding-left: 16px; border-left: 3px solid #000;">
          <p style="margin-bottom: 4px;">
            <strong>Affiliate / Partner Legal Name:</strong> 
            <span style="font-weight: bold; text-decoration: underline; margin-left: 5px;">${legalName}</span>
          </p>
          <p style="margin-bottom: 4px;">
            <strong>Entity Type:</strong> 
            ${check(entityType === "Individual")} Individual &nbsp;
            ${check(entityType === "LLC")} LLC &nbsp;
            ${check(entityType === "Corporation")} Corporation &nbsp;
            ${check(entityType === "Other")} Other
          </p>
          <p style="margin-bottom: 4px;">
            <strong>Mailing Address:</strong> 
            <span style="font-weight: bold; text-decoration: underline; margin-left: 5px;">${address}</span>
          </p>
          <p style="font-style: italic;">(“Affiliate/Partner”).</p>
        </div>
        <p>InsertBid and Affiliate/Partner may be referred to individually as a “Party” and collectively as the “Parties.”</p>
        <h6 style="font-weight: bold; margin-top: 24px; font-size: 14px;">1. PURPOSE</h6>
        <p>InsertBid operates an online auction and marketplace platform that enables users to buy, sell, trade, and collect items (the “Platform”). This Agreement governs Affiliate/Partner’s participation in InsertBid’s Affiliate Program, Partner Program, and, if applicable, Tag-Team Storefront Program, including promotion, attribution, compensation, performance expectations, intellectual property usage, compliance obligations, and termination rights.</p>
        
        <hr style="margin: 40px 0; border: 0; border-top: 1px solid #000;" />
        <h6 style="font-weight: bold; text-align: center; margin-bottom: 24px; font-size: 14px;">19. SIGNATURES</h6>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="width: 50%; vertical-align: top; padding-right: 20px;">
              <p style="font-weight: bold; margin-bottom: 4px;">INSERTBID, LLC</p>
              <div style="border-bottom: 1px solid #000; height: 30px; margin-bottom: 4px;">
               <span style="font-family: 'Times New Roman', serif; font-style: italic; font-size: 18px;">/s/ Jorge Verdejo</span>
              </div>
              <p style="font-size: 12px; color: #666; margin-bottom: 10px;">Signature</p>
              <p style="margin: 0;">Name: <span style="font-weight: bold;">Jorge Verdejo</span></p>
              <p style="margin: 0;">Title: <span style="font-weight: bold;">CEO</span></p>
              <p style="margin: 0;">Date: <span style="font-weight: bold;">${signedAt}</span></p>
            </td>
            <td style="width: 50%; vertical-align: top; padding-left: 20px;">
              <p style="font-weight: bold; margin-bottom: 4px;">AFFILIATE / PARTNER</p>
              <div style="background-color: #f8f9fa; border-bottom: 1px solid #000; height: 30px; margin-bottom: 4px; display: flex; align-items: flex-end; padding-left: 8px; font-style: italic; color: #0d6efd;">
                  /s/ ${signedName}
              </div>
              <p style="font-size: 12px; color: #666; margin-bottom: 10px;">Signature</p>
              <p style="margin: 0;">Name: <span style="font-weight: bold;">${legalName}</span></p>
              <p style="margin: 0;">Title: <span style="font-weight: bold;">Affiliate</span></p>
              <p style="margin: 0;">Date: <span style="font-weight: bold;">${signedAt}</span></p>
              <p style="margin: 0; font-size: 10px; color: #999;">IP: ${ipAddress}</p>
            </td>
          </tr>
        </table>
      </div>
    `;

    const opt = {
      margin: 0.5,
      filename: `InsertBid_Partner_Agreement_${legalName.replace(/\s+/g, "_")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  if (!affiliate) return null;

  const handlePayoutSubmit = async () => {
    setIsPaying(true);
    await onRecordPayout(affiliate._id, {
      amount: payoutAmount,
      method: payoutMethod,
      reference: payoutRef,
    });
    setIsPaying(false);
    onHide();
  };

  const handleSaveSettings = async () => {
    const payload = {
      campaignStartDate: campaignStart,
      campaignEndDate: campaignEnd,
      adminNotes: notes,
      commissionRules: rules,
    };
    await onUpdateSettings(affiliate._id, payload);
    onHide();
  };

  const updateRule = (category, field, value) => {
    setRules((prev) => ({
      ...prev,
      [category]: { ...prev[category], [field]: value },
    }));
  };

  const renderSocials = (socialsString) => {
    if (!socialsString)
      return <span className="text-muted">None provided</span>;
    return <span>{socialsString}</span>;
  };

  const referralLink = `https://insertbid.com/auth?ref=${affiliate.referralCode}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  const renderOverviewDetails = () => {
    if (loadingStats)
      return (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      );
    if (!details) return null;

    switch (selectedOverviewTab) {
      case "signups":
        return (
          <div className="mt-4">
            <h6 className="mb-3">Referred Users List</h6>
            <Table hover responsive className="align-middle bg-white border">
              <thead className="bg-light">
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Joined Date</th>
                </tr>
              </thead>
              <tbody>
                {details.signupsList?.length > 0 ? (
                  details.signupsList.map((u) => (
                    <tr key={u._id}>
                      <td className="fw-bold">{u.user_name}</td>
                      <td>{u.email}</td>
                      <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-muted">
                      No signups yet
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        );
      case "volume":
        return (
          <div className="mt-4">
            <h6 className="mb-3">Referral Purchase History</h6>
            <Table hover responsive className="align-middle bg-white border">
              <thead className="bg-light">
                <tr>
                  <th>Item</th>
                  <th>Buyer (Referral)</th>
                  <th>Sold Price</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {details.referralPurchasesList?.length > 0 ? (
                  details.referralPurchasesList.map((i) => (
                    <tr key={i._id}>
                      <td>{i.item_name}</td>
                      <td>{i.sold_to?.user_name}</td>
                      <td className="text-success fw-bold">
                        ${i.sold_price?.toFixed(2)}
                      </td>
                      <td>{new Date(i.date_of_sale).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-muted">
                      No purchases yet
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        );
      case "personal_listings":
        return (
          <div className="mt-4">
            <h6 className="mb-3">Affiliate's Listings</h6>
            <Table hover responsive className="align-middle bg-white border">
              <thead className="bg-light">
                <tr>
                  <th>Item</th>
                  <th>Status</th>
                  <th>Price/Bid</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {details.personalListingsList?.length > 0 ? (
                  details.personalListingsList.map((i) => (
                    <tr key={i._id}>
                      <td>{i.item_name}</td>
                      <td>
                        <Badge bg={i.item_sold ? "success" : "secondary"}>
                          {i.item_sold ? "SOLD" : "ACTIVE"}
                        </Badge>
                      </td>
                      <td>${i.sold_price?.toFixed(2) || 0}</td>
                      <td>{new Date(i.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-muted">
                      No listings found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        );
      case "commissions":
        return (
          <div className="mt-4">
            <h6 className="mb-3">Commission History Log</h6>
            <Table hover responsive className="align-middle bg-white border">
              <thead className="bg-light">
                <tr>
                  <th>Date</th>
                  <th>Description / Source</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {details.commissionLogList?.length > 0 ? (
                  details.commissionLogList.map((log) => {
                    let badgeBg = "warning"; 
                    if (log.status === "available") badgeBg = "success";
                    if (log.status === "paid") badgeBg = "secondary";
                    if (log.status === "voided") badgeBg = "danger";

                    const canVoid =
                      log.status === "pending" || log.status === "available";

                    return (
                      <tr
                        key={log._id}
                        className={
                          log.status === "voided"
                            ? "table-secondary text-muted"
                            : ""
                        }
                      >
                        <td>
                          {new Date(log.createdAt).toLocaleDateString()}{" "}
                          <small
                            className={
                              log.status === "voided"
                                ? "text-muted"
                                : "text-secondary"
                            }
                          >
                            {new Date(log.createdAt).toLocaleTimeString()}
                          </small>
                        </td>
                        <td>
                          <div
                            className={
                              log.status === "voided"
                                ? "text-decoration-line-through"
                                : ""
                            }
                          >
                            {log.description || log.type}
                          </div>
                          {log.relatedItem && (
                            <small
                              className={
                                log.status === "voided"
                                  ? "text-muted"
                                  : "text-secondary"
                              }
                            >
                              Item Ref: {log.relatedItem}
                            </small>
                          )}
                        </td>
                        <td
                          className={`fw-bold ${log.status === "voided" ? "text-muted text-decoration-line-through" : "text-success"}`}
                        >
                          +${log.amount?.toFixed(2)}
                        </td>
                        <td>
                          <Badge bg={badgeBg}>
                            {log.status?.toUpperCase() || "EARNED"}
                          </Badge>
                        </td>
                        <td className="text-end">
                          {canVoid && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleVoidCommission(log._id)}
                              disabled={voidingLogId === log._id}
                            >
                              {voidingLogId === log._id ? (
                                <Spinner size="sm" />
                              ) : (
                                "Void"
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted">
                      No commission records found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      contentClassName="bg-white"
    >
      <Modal.Header
        closeButton
        className="bg-white border-bottom align-items-center"
      >
        <div className="d-flex flex-column flex-lg-row align-items-lg-center w-100 gap-3 pe-3">
          <Modal.Title className="h5 mb-0 text-nowrap">
            Manage Affiliate:{" "}
            <span className="text-primary fw-bold">
              {affiliate.referralCode}
            </span>
          </Modal.Title>
          <div
            className="bg-light border rounded-pill p-1 ps-3 d-flex align-items-center w-100"
            style={{ maxWidth: "400px" }}
          >
            <div
              className="text-truncate text-muted small me-2"
              style={{ fontFamily: "monospace", flex: 1 }}
            >
              {referralLink}
            </div>
            <Button
              variant="warning"
              size="sm"
              className="rounded-pill px-3 fw-bold text-nowrap"
              onClick={copyLink}
            >
              Copy Link
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="bg-white">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4 border-bottom-0 bg-white"
        >
          <Tab eventKey="overview" title="Overview" className="bg-white">
            <Row className="g-3">
              <Col md={3}>
                <Card
                  className={`h-100 shadow-sm ${selectedOverviewTab === "signups" ? "border-primary bg-light" : "border bg-white"}`}
                  style={{ cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => setSelectedOverviewTab("signups")}
                >
                  <Card.Body className="text-center py-4 rounded">
                    <div
                      className={`text-uppercase small fw-bold mb-2 ${selectedOverviewTab === "signups" ? "text-primary" : "text-muted"}`}
                    >
                      Total Signups
                    </div>
                    {loadingStats ? (
                      <Spinner size="sm" variant="primary" />
                    ) : (
                      <h2 className="fw-bold text-dark mb-0">
                        {stats?.signups || 0}
                      </h2>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className={`h-100 shadow-sm ${selectedOverviewTab === "volume" ? "border-primary bg-light" : "border bg-white"}`}
                  style={{ cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => setSelectedOverviewTab("volume")}
                >
                  <Card.Body className="text-center py-4 rounded">
                    <div
                      className={`text-uppercase small fw-bold mb-2 ${selectedOverviewTab === "volume" ? "text-primary" : "text-muted"}`}
                    >
                      Referred Volume
                    </div>
                    {loadingStats ? (
                      <Spinner size="sm" variant="success" />
                    ) : (
                      <h2 className="fw-bold text-success mb-0">
                        ${stats?.totalReferralSpend?.toLocaleString() || 0}
                      </h2>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className={`h-100 shadow-sm ${selectedOverviewTab === "personal_listings" ? "border-primary bg-light" : "border bg-white"}`}
                  style={{ cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => setSelectedOverviewTab("personal_listings")}
                >
                  <Card.Body className="text-center py-4 rounded">
                    <div
                      className={`text-uppercase small fw-bold mb-2 ${selectedOverviewTab === "personal_listings" ? "text-primary" : "text-muted"}`}
                    >
                      Their Listings
                    </div>
                    {loadingStats ? (
                      <Spinner size="sm" />
                    ) : (
                      <h2 className="fw-bold text-dark mb-0">
                        {stats?.personalListings || 0}
                      </h2>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className={`h-100 shadow-sm ${selectedOverviewTab === "commissions" ? "border-primary bg-light" : "border bg-white"}`}
                  style={{ cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => setSelectedOverviewTab("commissions")}
                >
                  <Card.Body className="text-center py-4 rounded">
                    <div
                      className={`text-uppercase small fw-bold mb-2 ${selectedOverviewTab === "commissions" ? "text-primary" : "text-muted"}`}
                    >
                      Commissions
                    </div>
                    {loadingStats ? (
                      <Spinner size="sm" />
                    ) : (
                      <h2 className="fw-bold text-success mb-0">
                        ${stats?.totalCommissions?.toFixed(2) || "0.00"}
                      </h2>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {renderOverviewDetails()}
          </Tab>

          <Tab eventKey="wallet" title="Wallet & Payouts" className="bg-white">
            <Row className="g-4">
              <Col md={6}>
                <Card className="shadow-sm border-0 bg-light h-100">
                  <Card.Header className="bg-white border-bottom fw-bold py-3">
                    Recent Payout History
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <Table hover className="mb-0 small align-middle">
                        <thead className="bg-light text-muted">
                          <tr>
                            <th className="border-0 ps-3">Date</th>
                            <th className="border-0">Amount</th>
                            <th className="border-0">Method / Ref</th>
                            <th className="border-0 pe-3 text-end">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {affiliate.payoutHistory &&
                          affiliate.payoutHistory.length > 0 ? (
                            affiliate.payoutHistory.map((payout) => (
                              <tr key={payout._id}>
                                <td className="ps-3 text-muted">
                                  {new Date(
                                    payout.createdAt,
                                  ).toLocaleDateString()}
                                </td>
                                <td className="fw-bold text-dark">
                                  ${payout.amount?.toFixed(2)}
                                </td>
                                <td>
                                  <div
                                    className="text-uppercase fw-bold"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {payout.paymentMethod}
                                  </div>
                                  <div
                                    className="text-muted text-truncate"
                                    style={{ maxWidth: "100px" }}
                                    title={payout.transactionReference}
                                  >
                                    {payout.transactionReference || "-"}
                                  </div>
                                </td>
                                <td className="pe-3 text-end">
                                  <Badge bg="success" className="fw-normal">
                                    PAID
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="4"
                                className="text-center py-4 text-muted"
                              >
                                No payout history found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                    <div className="p-3 border-top bg-white mt-auto">
                      <small className="text-muted fw-bold d-block mb-2 text-uppercase">
                        User Payment Details:
                      </small>
                      {affiliate.payoutSettings ? (
                        <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded border">
                          <div>
                            <Badge bg="secondary" className="me-2">
                              {affiliate.payoutSettings.method?.toUpperCase()}
                            </Badge>
                            <span className="fw-bold text-dark small">
                              {affiliate.payoutSettings.identifier ||
                                affiliate.payoutSettings.email}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <Alert variant="warning" className="mb-0 py-2 small">
                          No payment details set by user.
                        </Alert>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="text-center border shadow-sm bg-white mb-3">
                  <Card.Body className="py-4">
                    <h6 className="text-muted text-uppercase small ls-1 fw-bold">
                      Available Balance
                    </h6>
                    <h1 className="text-success display-5 fw-bold my-3">
                      $
                      {stats?.availableBalance?.toFixed(2) ||
                        affiliate.wallet?.availableBalance?.toFixed(2) ||
                        "0.00"}
                    </h1>
                    <div className="d-flex justify-content-between pt-2">
                      <span className="text-muted small">
                        Pending:{" "}
                        <span className="text-dark fw-bold">
                          $
                          {stats?.pendingBalance?.toFixed(2) ||
                            affiliate.wallet?.pendingBalance?.toFixed(2) ||
                            "0.00"}
                        </span>
                      </span>
                      <span className="text-muted small">
                        Paid:{" "}
                        <span className="text-dark fw-bold">
                          ${affiliate.wallet?.totalPaid?.toFixed(2) || "0.00"}
                        </span>
                      </span>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border shadow-sm bg-white">
                  <Card.Header className="bg-white fw-bold py-2 border-bottom text-success">
                    Record Manual Payout
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Amount ($)</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            value={payoutAmount}
                            onChange={(e) => setPayoutAmount(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Method</Form.Label>
                          <Form.Select
                            size="sm"
                            value={payoutMethod}
                            onChange={(e) => setPayoutMethod(e.target.value)}
                          >
                            <option value="paypal">PayPal</option>
                            <option value="zelle">Zelle</option>
                            <option value="check">Check</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="small">
                            Transaction Reference
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="e.g. PayPal ID..."
                            value={payoutRef}
                            onChange={(e) => setPayoutRef(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Button
                          variant="success"
                          size="sm"
                          className="w-100 mt-2"
                          onClick={handlePayoutSubmit}
                          disabled={isPaying || payoutAmount <= 0}
                        >
                          {isPaying ? (
                            <Spinner size="sm" />
                          ) : (
                            "Confirm Payout Record"
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* ... Keep commissions_rules, settings, and application_info exactly as they were ... */}
          <Tab eventKey="commissions_rules" title="Commission Rules">
            <div className="bg-white p-4 rounded shadow-sm border">
              <div className="d-flex flex-column gap-3 bg-white">
                <Card className="border bg-white">
                  <Card.Body className="bg-white rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Check
                        type="switch"
                        id="signup-switch"
                        label={
                          <span className="fw-bold">
                            Signup Reward (One-time)
                          </span>
                        }
                        checked={rules.signupReward.enabled}
                        onChange={(e) =>
                          updateRule(
                            "signupReward",
                            "enabled",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                    {rules.signupReward.enabled && (
                      <div className="d-flex align-items-center gap-3 ms-4 mt-2 bg-light p-2 rounded">
                        <InputGroup size="sm" style={{ maxWidth: "150px" }}>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="number"
                            placeholder="0"
                            value={
                              rules.signupReward.amount === 0
                                ? ""
                                : rules.signupReward.amount
                            }
                            onChange={(e) =>
                              updateRule(
                                "signupReward",
                                "amount",
                                e.target.value === ""
                                  ? 0
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </InputGroup>
                        <small className="text-muted">
                          Paid when referral creates an account.
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                <Card className="border bg-white">
                  <Card.Body className="bg-white rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Check
                        type="switch"
                        id="bp-switch"
                        label={
                          <span className="fw-bold">
                            Buyer Premium Share (%)
                          </span>
                        }
                        checked={rules.buyerPremiumReward.enabled}
                        onChange={(e) =>
                          updateRule(
                            "buyerPremiumReward",
                            "enabled",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                    {rules.buyerPremiumReward.enabled && (
                      <div className="d-flex align-items-center gap-3 ms-4 mt-2 bg-light p-2 rounded">
                        <InputGroup size="sm" style={{ maxWidth: "150px" }}>
                          <Form.Control
                            type="number"
                            placeholder="0"
                            value={
                              rules.buyerPremiumReward.percentage === 0
                                ? ""
                                : rules.buyerPremiumReward.percentage
                            }
                            onChange={(e) =>
                              updateRule(
                                "buyerPremiumReward",
                                "percentage",
                                e.target.value === ""
                                  ? 0
                                  : Number(e.target.value),
                              )
                            }
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <small className="text-muted">
                          Earned on every purchase or sale the referral makes.
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                <Card className="border bg-white">
                  <Card.Body className="bg-white rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Check
                        type="switch"
                        id="listing-switch"
                        label={
                          <span className="fw-bold">
                            Affiliate Listing Reward
                          </span>
                        }
                        checked={rules.affiliateListingReward.enabled}
                        onChange={(e) =>
                          updateRule(
                            "affiliateListingReward",
                            "enabled",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                    {rules.affiliateListingReward.enabled && (
                      <div className="d-flex align-items-center gap-3 ms-4 mt-2 bg-light p-2 rounded">
                        <Form.Select
                          size="sm"
                          style={{ width: "auto" }}
                          value={rules.affiliateListingReward.type}
                          onChange={(e) =>
                            updateRule(
                              "affiliateListingReward",
                              "type",
                              e.target.value,
                            )
                          }
                        >
                          <option value="fixed">Fixed ($)</option>
                          <option value="percentage">
                            % of Buyer's Premium
                          </option>
                        </Form.Select>
                        <Form.Control
                          size="sm"
                          type="number"
                          style={{ width: "100px" }}
                          placeholder="0"
                          value={
                            rules.affiliateListingReward.value === 0
                              ? ""
                              : rules.affiliateListingReward.value
                          }
                          onChange={(e) =>
                            updateRule(
                              "affiliateListingReward",
                              "value",
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value),
                            )
                          }
                        />
                        <small className="text-muted">
                          Earned when the Affiliate lists/sells an item
                          themselves.
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </div>
          </Tab>

          <Tab eventKey="settings" title="Settings & Access">
            <div className="bg-white p-4 rounded shadow-sm border">
              <Row className="g-3 bg-white">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small text-muted fw-bold">
                      CAMPAIGN START
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={campaignStart}
                      onChange={(e) => setCampaignStart(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small text-muted fw-bold">
                      CAMPAIGN END
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={campaignEnd}
                      onChange={(e) => setCampaignEnd(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="small text-muted fw-bold">
                      ADMIN NOTES (INTERNAL)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <hr className="my-4" />
              <div className="d-flex justify-content-between align-items-center p-3 border rounded bg-light mb-3">
                <div>
                  <h6 className="mb-1 fw-bold text-dark">Signed Contract</h6>
                  <small className="text-muted d-block">
                    {affiliate.contract?.hasSigned
                      ? `Signed on ${new Date(affiliate.contract.signedAt).toLocaleDateString()}`
                      : "Contract has not been signed yet."}
                  </small>
                </div>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={downloadContractPDF}
                  disabled={!affiliate.contract?.hasSigned}
                >
                  <i className="bi bi-file-earmark-pdf me-2"></i>Download PDF
                </Button>
              </div>
              <div className="d-flex justify-content-between align-items-center p-3 border rounded bg-white">
                <div>
                  <h6 className="mb-1 fw-bold">Account Status</h6>
                  <Badge
                    bg={affiliate.status === "suspended" ? "danger" : "success"}
                  >
                    {affiliate.status.toUpperCase()}
                  </Badge>
                </div>
                {affiliate.status === "suspended" ? (
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => onStatusChange(affiliate._id, "approved")}
                  >
                    Re-Activate Account
                  </Button>
                ) : (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onStatusChange(affiliate._id, "suspended")}
                  >
                    Suspend Account
                  </Button>
                )}
              </div>
            </div>
          </Tab>

          <Tab eventKey="application_info" title="Application Info">
            <div className="bg-white p-4 rounded shadow-sm border">
              <Row className="g-4">
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-none">
                    <Card.Body className="p-0">
                      <h6 className="fw-bold text-uppercase text-muted small mb-3 border-bottom pb-2">
                        User Contact Info
                      </h6>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="px-0 py-2 border-0 d-flex justify-content-between">
                          <span className="text-muted">Email:</span>
                          <span className="fw-medium">
                            {affiliate.contactInfo?.email}
                          </span>
                        </ListGroup.Item>
                        <ListGroup.Item className="px-0 py-2 border-0 d-flex justify-content-between">
                          <span className="text-muted">Phone:</span>
                          <span className="fw-medium">
                            {affiliate.contactInfo?.phoneNumber}
                          </span>
                        </ListGroup.Item>
                        <ListGroup.Item className="px-0 py-2 border-0 d-flex justify-content-between">
                          <span className="text-muted">User ID:</span>
                          <small className="font-monospace text-muted">
                            {affiliate.userId?._id}
                          </small>
                        </ListGroup.Item>
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-none">
                    <Card.Body className="p-0">
                      <h6 className="fw-bold text-uppercase text-muted small mb-3 border-bottom pb-2">
                        Company / Store Details
                      </h6>
                      {affiliate.companyInfo &&
                      Object.keys(affiliate.companyInfo).length > 0 ? (
                        <ListGroup variant="flush">
                          <ListGroup.Item className="px-0 py-2 border-0 d-flex justify-content-between">
                            <span className="text-muted">Store Name:</span>
                            <span className="fw-medium">
                              {affiliate.companyInfo.storeName}
                            </span>
                          </ListGroup.Item>
                          <ListGroup.Item className="px-0 py-2 border-0 d-flex justify-content-between">
                            <span className="text-muted">Legal Name:</span>
                            <span className="fw-medium">
                              {affiliate.companyInfo.legalName}
                            </span>
                          </ListGroup.Item>
                          <ListGroup.Item className="px-0 py-2 border-0 d-flex justify-content-between">
                            <span className="text-muted">Website:</span>
                            <a
                              href={`https://${affiliate.companyInfo.website?.replace(/^https?:\/\//, "")}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary text-decoration-none"
                            >
                              {affiliate.companyInfo.website}
                            </a>
                          </ListGroup.Item>
                          <ListGroup.Item className="px-0 py-2 border-0 d-flex justify-content-between">
                            <span className="text-muted">Social Media:</span>
                            <span className="fw-medium">
                              {renderSocials(affiliate.companyInfo.socialMedia)}
                            </span>
                          </ListGroup.Item>
                        </ListGroup>
                      ) : (
                        <div className="text-muted small fst-italic py-2">
                          No company information provided.
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={12}>
                  <Card className="border bg-light">
                    <Card.Body>
                      <h6 className="fw-bold text-uppercase text-muted small mb-3">
                        Application Responses
                      </h6>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="mb-2">
                            <span className="d-block small text-muted fw-bold mb-1">
                              Promotion Method / Description
                            </span>
                            <div className="p-2 bg-white rounded border text-dark small">
                              {affiliate.applicationDetails?.promotionMethod ||
                                "N/A"}
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-2">
                            <span className="d-block small text-muted fw-bold mb-1">
                              Target Audience
                            </span>
                            <div className="p-2 bg-white rounded border text-dark small">
                              {affiliate.applicationDetails?.targetAudience ||
                                "N/A"}
                            </div>
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="mb-2">
                            <span className="d-block small text-muted fw-bold mb-1">
                              Provided Website / Portfolio
                            </span>
                            <div className="p-2 bg-white rounded border text-dark small">
                              {affiliate.applicationDetails?.websiteUrl ? (
                                <a
                                  href={affiliate.applicationDetails.websiteUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {affiliate.applicationDetails.websiteUrl}
                                </a>
                              ) : (
                                "N/A"
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="bg-white">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        {activeTab === "commissions_rules" && (
          <Button variant="primary" onClick={handleSaveSettings}>
            Save Commission Rules
          </Button>
        )}
        {activeTab === "settings" && (
          <Button variant="primary" onClick={handleSaveSettings}>
            Save Settings
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default function AffiliateManager() {
  const [activeAffiliates, setActiveAffiliates] = useState([]);
  const [pendingApplications, setPendingApplications] = useState([]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    affiliate: true,
    partner: true,
    storefront: true,
  });

  const [showManageModal, setShowManageModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Mocking your action calls here
      const [activeData, pendingData] = await Promise.all([
        get_affiliates_action("active", searchTerm),
        get_affiliates_action("pending", searchTerm),
      ]);
      setActiveAffiliates(activeData);
      setPendingApplications(pendingData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const handleCreate = async (data) => {
    await create_affiliate_action(data);
    fetchData();
  };

  const handleReviewSubmission = async (id, reviewData) => {
    await update_affiliate_status_action(id, reviewData);
    setShowReviewModal(false);
    fetchData();
  };

  const handleFilterChange = (type) => {
    setFilters((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "storefront":
        return "info";
      case "partner":
        return "warning";
      case "affiliate":
      default:
        return "secondary";
    }
  };

  const filteredActiveAffiliates = activeAffiliates.filter((aff) => {
    const type = aff.programType || "affiliate";
    return filters[type];
  });

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Affiliate Manager</h2>
        <div className="d-flex gap-2">
          <InputGroup>
            <InputGroup.Text>🔍</InputGroup.Text>
            <Form.Control
              placeholder="Search user/code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + Create New
          </Button>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        {/* here  */}
        <Tab
          eventKey="active"
          className="bg-light"
          title={`Active (${activeAffiliates.length})`}
        >
          <div className="d-flex gap-4 mb-3 align-items-center p-2 bg-light rounded">
            <span className="fw-bold text-muted small text-uppercase">
              Filter by Type:
            </span>
            <Form.Check
              type="switch"
              id="filter-affiliate"
              label="Affiliates"
              checked={filters.affiliate}
              onChange={() => handleFilterChange("affiliate")}
            />
            <Form.Check
              type="switch"
              id="filter-partner"
              label="Partners"
              checked={filters.partner}
              onChange={() => handleFilterChange("partner")}
            />
            <Form.Check
              type="switch"
              id="filter-storefront"
              label="Storefronts"
              checked={filters.storefront}
              onChange={() => handleFilterChange("storefront")}
            />
          </div>

          {loading ? (
            <Spinner animation="border" />
          ) : (
            <Card className="shadow-sm border-0">
              <Table hover responsive className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>User</th>
                    <th>Code</th>
                    <th>Wallet</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...filteredActiveAffiliates]
                    .sort((a, b) => {
                      if (a.payoutRequested === b.payoutRequested) return 0;
                      return a.payoutRequested ? -1 : 1;
                    })
                    .map((aff) => (
                      <tr
                        key={aff._id}
                        className={aff.payoutRequested ? "table-warning" : ""}
                      >
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="fw-bold">
                              {aff.userId?.user_name}
                            </div>
                            <Badge
                              bg={getTypeBadgeColor(
                                aff.programType || "affiliate",
                              )}
                              className="text-uppercase"
                              style={{ fontSize: "0.65rem" }}
                            >
                              {aff.programType || "affiliate"}
                            </Badge>
                          </div>
                          <small className="text-muted">
                            {aff.userId?.email}
                          </small>
                        </td>
                        <td onClick={() => console.log(aff)}>
                          <Badge bg="light" text="dark" className="border">
                            {aff.referralCode}
                          </Badge>
                        </td>
                        <td>
                          <div className="text-success fw-bold">
                            ${aff.wallet?.availableBalance?.toFixed(2)}
                          </div>
                          <small className="text-muted d-block">
                            Paid: ${aff.wallet?.totalPaid?.toFixed(2)}
                          </small>

                          {aff.payoutRequested && (
                            <div className="mt-1">
                              <Badge bg="danger" className="text-uppercase">
                                Payout Requested
                              </Badge>
                              <div
                                style={{ fontSize: "0.75rem" }}
                                className="text-dark mt-1 fw-bold"
                              >
                                <i className="bi bi-clock me-1"></i>
                                {new Date(
                                  aff.payoutRequestedAt,
                                ).toLocaleDateString()}{" "}
                                {new Date(
                                  aff.payoutRequestedAt,
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge
                            bg={
                              aff.status === "suspended" ? "danger" : "success"
                            }
                          >
                            {aff.status}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant={
                              aff.payoutRequested
                                ? "success"
                                : "outline-primary"
                            }
                            onClick={() => {
                              setSelectedItem(aff);
                              setShowManageModal(true);
                            }}
                          >
                            {aff.payoutRequested ? "Process Payout" : "Manage"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  {filteredActiveAffiliates.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No affiliates found matching the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card>
          )}
        </Tab>

        <Tab
          eventKey="pending"
          title={`Pending (${pendingApplications.length})`}
        >
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <Card className="shadow-sm border-0">
              <Table hover responsive className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>User</th>
                    <th>Proposed Code</th>
                    <th>Method</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingApplications.map((app) => (
                    <tr key={app._id}>
                      <td>{app.userId?.user_name}</td>
                      <td>{app.referralCode}</td>
                      <td>
                        {app.applicationDetails?.promotionMethod?.substring(
                          0,
                          30,
                        )}
                        ...
                      </td>
                      <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            setSelectedItem(app);
                            setShowReviewModal(true);
                          }}
                        >
                          Review Application
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {pendingApplications.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted">
                        No pending applications found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card>
          )}
        </Tab>
      </Tabs>

      <CreateAffiliateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />

      <ManageAffiliateModal
        show={showManageModal}
        onHide={() => setShowManageModal(false)}
        affiliate={selectedItem}
        onRecordPayout={async (id, data) => {
          await record_payout_action(id, data);
          fetchData();
        }}
        onUpdateSettings={async (id, data) => {
          await update_campaign_settings_action(id, data);
          fetchData();
        }}
        onStatusChange={async (id, status) => {
          await update_campaign_settings_action(id, { status: status });
          fetchData();
          setSelectedItem((prev) => ({ ...prev, status: status }));
        }}
      />

      <ReviewApplicationModal
        show={showReviewModal}
        onHide={() => setShowReviewModal(false)}
        application={selectedItem}
        onReview={handleReviewSubmission}
      />
    </div>
  );
}
